/**
 * AI credits — client reads and master top-ups.
 *
 * The charge side lives server-only (api/_lib/chargeAiCredit via the
 * fixed-amount RPC); nothing here can spend. Advisory system: balances may be
 * negative and the UI warns instead of blocking
 * (docs/superpowers/specs/2026-09-05-ai-credits-design.md).
 */
import { getSupabaseClient } from "@/config/supabase";
import { nullableArg } from "./dbBoundary";
import { logError } from "@/services/logger";

/** Same env var the server charge gate reads — one switch, both sides. */
export const isAiCreditsEnabled = (): boolean =>
  import.meta.env.VITE_AI_CREDITS_ENABLED === "true";

/** Balance for the chat chip. Member-or-master gated inside the RPC. */
export const fetchCreditBalance = async (workspaceId: string): Promise<number | null> => {
  const { data, error } = await getSupabaseClient().rpc("get_credit_balance", {
    p_workspace: workspaceId,
  });
  if (error) {
    logError("CreditService", "Error fetching credit balance", error);
    return null;
  }
  // null means the RPC answered with nothing — not a balance of zero. The two
  // read as opposite messages downstream: the chip treats null as "unknown"
  // and hides itself, while 0 is "depleted" and turns the balance red. A
  // funded workspace whose read came back empty must not be shown as empty.
  if (data === null || data === undefined) {
    logError("CreditService", "Credit balance RPC returned no value", { workspaceId });
    return null;
  }
  const balance = Number(data);
  return Number.isFinite(balance) ? balance : null;
};

export interface WorkspaceCreditBalance {
  workspaceId: string;
  workspaceName: string;
  balance: number;
  lastTopupAt: string | null;
  /** Credits charged per successful AI call for this workspace. */
  rate: number;
  /** true = set for this workspace; false = inherited from the global default. */
  rateIsOverride: boolean;
}

/** Admin table — master-only inside the RPC; others get zero rows. */
export const fetchCreditBalances = async (): Promise<WorkspaceCreditBalance[]> => {
  const { data, error } = await getSupabaseClient().rpc("credit_balances");
  if (error) {
    logError("CreditService", "Error fetching credit balances", error);
    throw new Error(`Failed to fetch credit balances: ${error.message}`);
  }
  return ((data ?? []) as Array<Record<string, unknown>>)
    // A row with no workspace_id became the literal string "null" — used as a
    // React key and, worse, as the target a top-up would be written against.
    // A row that cannot name its workspace is not a workspace to act on.
    .filter((r) => typeof r.workspace_id === "string" && r.workspace_id.length > 0)
    .map((r) => ({
      workspaceId: String(r.workspace_id),
      workspaceName: String(r.workspace_name ?? ""),
      balance: Number(r.balance ?? 0),
      lastTopupAt: (r.last_topup_at as string | null) ?? null,
      rate: Number(r.rate ?? 1),
      rateIsOverride: Boolean(r.rate_is_override),
    }));
};

/**
 * Credits charged per successful AI call for this workspace — the override if
 * one is set, otherwise the global default, otherwise 1. Member-gated inside
 * the RPC. The UI states this rather than assuming 1.
 */
export const fetchCreditRate = async (workspaceId: string): Promise<number | null> => {
  const { data, error } = await getSupabaseClient().rpc("get_credit_rate", {
    p_workspace: workspaceId,
  });
  if (error) {
    logError("CreditService", "Error fetching credit rate", error);
    return null;
  }
  // Same rule as the balance, and the same reason: a rate nobody could read is
  // unknown, not 1. Assuming 1 would let a surface quote a price the workspace
  // is not on — the trap CreditChip was already fixed for.
  if (data === null || data === undefined) {
    logError("CreditService", "Credit rate RPC returned no value", { workspaceId });
    return null;
  }
  const rate = Number(data);
  return Number.isFinite(rate) ? rate : null;
};

/** The default every workspace without an override inherits. Master-only. */
export const fetchGlobalCreditRate = async (): Promise<number | null> => {
  const { data, error } = await getSupabaseClient().rpc("global_credit_rate");
  if (error) {
    logError("CreditService", "Error fetching global credit rate", error);
    return null;
  }
  return data === null || data === undefined ? null : Number(data);
};

/**
 * Master sets a rate: pass null as the workspace to move the global default,
 * a workspace id to override it for that workspace. The master check lives in
 * the database — a non-master gets a thrown error, not a silent no-op, because
 * a rate that did not take effect must never look like one that did.
 */
export const setCreditRate = async (workspaceId: string | null, rate: number): Promise<void> => {
  if (!(rate >= 0)) throw new Error("Rate must be zero or greater");
  // p_workspace NULL is the GLOBAL rate, not a missing value — see dbBoundary.
  // (The note lives out here on purpose: a comment inside the argument literal
  // hides the key after it from the rpcArgumentNames guard.)
  const { error } = await getSupabaseClient().rpc("set_credit_rate", {
    p_workspace: nullableArg(workspaceId),
    p_rate: rate,
  });
  if (error) {
    logError("CreditService", "Error setting credit rate", error);
    throw new Error(`Failed to set rate: ${error.message}`);
  }
};

/** Drop a workspace's override so it inherits the global default again. */
export const clearCreditRate = async (workspaceId: string): Promise<void> => {
  const { error } = await getSupabaseClient().rpc("clear_credit_rate", {
    p_workspace: workspaceId,
  });
  if (error) {
    logError("CreditService", "Error clearing credit rate", error);
    throw new Error(`Failed to clear rate: ${error.message}`);
  }
};

export interface CreditLedgerRow {
  id: string;
  workspaceId: string;
  amount: number;
  reason: string;
  note: string | null;
  createdAt: string;
}

/** Recent ledger for the admin page — RLS scopes what the caller may see. */
export const fetchRecentTransactions = async (limit = 30): Promise<CreditLedgerRow[]> => {
  const { data, error } = await getSupabaseClient()
    .from("credit_transactions")
    .select("id, workspace_id, amount, reason, note, created_at")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    logError("CreditService", "Error fetching credit ledger", error);
    throw new Error(`Failed to fetch credit ledger: ${error.message}`);
  }
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id),
    workspaceId: String(r.workspace_id),
    amount: Number(r.amount ?? 0),
    reason: String(r.reason ?? ""),
    note: (r.note as string | null) ?? null,
    createdAt: String(r.created_at ?? ""),
  }));
};

/**
 * Master top-up: a direct insert under the RLS policy (master + own uid +
 * positive amount). A non-master hits the policy and gets a clear error —
 * enforcement lives in the database, not here.
 */
export const topUpCredits = async (input: {
  workspaceId: string;
  amount: number;
  note: string;
  userId: string;
}): Promise<void> => {
  const { workspaceId, amount, note, userId } = input;
  if (!(amount > 0)) throw new Error("Top-up amount must be positive");
  const { error } = await getSupabaseClient().from("credit_transactions").insert({
    workspace_id: workspaceId,
    amount,
    reason: "topup",
    note: note || null,
    created_by: userId,
  });
  if (error) {
    logError("CreditService", "Error topping up credits", error);
    throw new Error(`Failed to top up: ${error.message}`);
  }
};
